// App del peoneta (docs/111, pedido de Rodrigo 2026-10-25). En el celular del vehículo: el peoneta
// entra con SU usuario, ve sus ODD en ruta, marca cada parada (un toque si fue completa), registra lo
// que cobra (efectivo, cheque con foto, transferencia con foto del comprobante) y rinde. Lo cobrado NO
// baja la deuda del cliente: eso ocurre cuando tesorería recibe los valores. Sin señal, lo registrado
// queda en el celular y se envía solo al volver (cada envío lleva su clave: no se duplica).
// Vive en /app/reparto/ (#/ruta), fuera del shell de escritorio, y se instala en el celular como
// «MADECI Reparto» (2026-10-28; /app/ruta/ es la del vendedor). Sin señal abre con la última lista.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { api, ErrorApi, nuevaClaveIdempotencia, pedirArchivo } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { fecha, fechaHora, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import './ruta.css';

type Medio = 'EFECTIVO' | 'CHEQUE' | 'TRANSFERENCIA';
type Modo = 'CONTADO' | 'COBRANZA' | 'CREDITO';
interface DocumentoACobrar {
  documentoId: string;
  tipoDte: number;
  folio: string | null;
  fechaVencimiento: string;
  diasVencido: number;
  saldo: string;
  notaNumero: string;
  deEstaEntrega: boolean;
}
interface Cobro {
  cobroId: string;
  medio: Medio;
  monto: string;
  referencia: string | null;
  banco: string | null;
  numeroDocumento: string | null;
  fechaDocumento: string | null;
  anulado: boolean;
  tieneFoto: boolean;
  estadoTesoreria: string | null;
}
interface Linea {
  notaLineaId: string;
  sku: string;
  descripcion: string;
  cargado: string | null;
  entregado: string | null;
}
interface Parada {
  notaId: string;
  parada: number;
  numeroVisible: string;
  cliente: string;
  direccion: string | null;
  zona: string | null;
  estado: string;
  marcada: boolean;
  lineas: Linea[];
  aCobrar: { modo: Modo; total: string; documentos: DocumentoACobrar[] };
  cobros: Cobro[];
}
interface OdRuta {
  odId: string;
  numero: number;
  nombre: string | null;
  estado: string;
  version: number;
  fechaNegocio: string;
  patente: string | null;
  paradas: Parada[];
}
interface MisRutas {
  personal: { id: string; nombre: string } | null;
  ordenes: OdRuta[];
}

// ---------------------------------------------------------------- cola sin señal

interface Pendiente {
  id: string;
  odId: string;
  notaId: string;
  tipo: 'entrega' | 'cobro';
  ruta: string;
  cuerpo: Record<string, unknown>;
  clave: string;
  error?: string;
  /** Ya lo recibió el servidor: sigue a la vista hasta que la lista nueva lo traiga. */
  enviado?: boolean;
}

/** ¿La parada que mandó el servidor ya trae este registro? */
const yaEnLaParada = (x: Pendiente, p: Parada) =>
  x.tipo === 'cobro' ? p.cobros.some((c) => c.cobroId === x.cuerpo['cobroId']) : p.marcada;

const claveCola = (empresaId: string) => `madeci:ruta:cola:${empresaId}`;
function leerCola(empresaId: string): Pendiente[] {
  try {
    const t = window.localStorage.getItem(claveCola(empresaId));
    return t ? (JSON.parse(t) as Pendiente[]) : [];
  } catch {
    return [];
  }
}
function guardarCola(empresaId: string, cola: Pendiente[]) {
  try {
    window.localStorage.setItem(claveCola(empresaId), JSON.stringify(cola));
  } catch {
    // Sin almacenamiento (modo privado o lleno): la cola sigue en memoria mientras la pantalla viva.
  }
}

/** Códigos que significan «ya estaba hecho»: se dan por enviados. */
const YA_HECHO = new Set(['ENTREGA_YA_REGISTRADA']);

function useCola(empresaId: string, alEnviar: () => void) {
  const [cola, setCola] = useState<Pendiente[]>(() => leerCola(empresaId));
  const [enLinea, setEnLinea] = useState(() => navigator.onLine);
  const enviando = useRef(false);
  const actualizar = useCallback(
    (f: (c: Pendiente[]) => Pendiente[]) =>
      setCola((c) => {
        const n = f(c);
        guardarCola(empresaId, n);
        return n;
      }),
    [empresaId],
  );
  // Lo enviado no se borra al tiro: si la lista nueva tarda (poca señal), el cobro desaparecía y el
  // monto volvía al total, y el peoneta lo registraba de nuevo o no podía cobrar el resto con otro medio.
  const marcarEnviado = useCallback(
    (id: string) => actualizar((c) => c.map((x) => (x.id === id ? { ...x, enviado: true } : x))),
    [actualizar],
  );
  const enviar = useCallback(async () => {
    if (enviando.current) return;
    enviando.current = true;
    let algo = false;
    try {
      for (const p of leerCola(empresaId).filter((x) => !x.enviado)) {
        try {
          await api.post(p.ruta, p.cuerpo, { idempotencia: p.clave });
          marcarEnviado(p.id);
          algo = true;
          setEnLinea(true);
        } catch (e) {
          if (e instanceof ErrorApi && e.status === 0) {
            setEnLinea(false);
            break;
          }
          if (e instanceof ErrorApi && YA_HECHO.has(e.codigo)) {
            marcarEnviado(p.id);
            algo = true;
            continue;
          }
          const mensaje = e instanceof Error ? e.message : String(e);
          actualizar((c) => c.map((x) => (x.id === p.id ? { ...x, error: mensaje } : x)));
        }
      }
    } finally {
      enviando.current = false;
      if (algo) alEnviar();
    }
  }, [empresaId, actualizar, marcarEnviado, alEnviar]);
  /** Con la lista nueva del servidor, suelta lo enviado que ya trae (o cuya ODD o parada ya no está). */
  const conciliar = useCallback(
    (ordenes: OdRuta[]) => {
      const sobra = (x: Pendiente) => {
        const p = ordenes
          .find((o) => o.odId === x.odId)
          ?.paradas.find((y) => y.notaId === x.notaId);
        return !!x.enviado && (!p || yaEnLaParada(x, p));
      };
      if (leerCola(empresaId).some(sobra)) actualizar((c) => c.filter((x) => !sobra(x)));
    },
    [empresaId, actualizar],
  );
  useEffect(() => {
    void enviar();
    const t = window.setInterval(() => void enviar(), 20_000);
    const online = () => {
      setEnLinea(true);
      void enviar();
    };
    const offline = () => setEnLinea(false);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    return () => {
      window.clearInterval(t);
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
    };
  }, [enviar]);
  const agregar = (p: Omit<Pendiente, 'id' | 'clave'>) => {
    actualizar((c) => [
      ...c,
      { ...p, id: crypto.randomUUID(), clave: nuevaClaveIdempotencia(p.tipo) },
    ]);
    window.setTimeout(() => void enviar(), 0);
  };
  const descartar = (id: string) => actualizar((c) => c.filter((x) => x.id !== id));
  const porEnviar = cola.filter((x) => !x.enviado).length;
  return { cola, porEnviar, enLinea, agregar, descartar, enviar, conciliar };
}

// ---------------------------------------------------------------- foto reducida

/** Reduce la foto en el celular (lado mayor 1280 px, JPEG 0,7): ~200 KB en vez de varios MB. */
async function reducirFoto(archivo: File): Promise<{ tipoMime: 'image/jpeg'; base64: string }> {
  const url = URL.createObjectURL(archivo);
  try {
    const img = await new Promise<HTMLImageElement>((ok, mal) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = () => mal(new Error('No se pudo leer la foto'));
      i.src = url;
    });
    const escala = Math.min(1, 1280 / Math.max(img.width, img.height));
    const lienzo = document.createElement('canvas');
    lienzo.width = Math.round(img.width * escala);
    lienzo.height = Math.round(img.height * escala);
    lienzo.getContext('2d')!.drawImage(img, 0, 0, lienzo.width, lienzo.height);
    const datos = lienzo.toDataURL('image/jpeg', 0.7);
    return { tipoMime: 'image/jpeg', base64: datos.slice(datos.indexOf(',') + 1) };
  } finally {
    URL.revokeObjectURL(url);
  }
}

const soloDigitos = (t: string) => t.replace(/\D/g, '');
const NOMBRE_MEDIO: Record<Medio, string> = {
  EFECTIVO: 'Efectivo',
  CHEQUE: 'Cheque',
  TRANSFERENCIA: 'Transferencia',
};
const NOMBRE_MODO: Record<Modo, string> = {
  CONTADO: 'Contado',
  COBRANZA: 'Cobranza',
  CREDITO: 'Crédito',
};
const NOMBRE_ESTADO: Record<string, string> = {
  ENTREGADA: 'Entregada',
  ENTREGA_PARCIAL: 'Parcial',
  RECHAZADA: 'Rechazada',
};

// ---------------------------------------------------------------- instalación y sin señal

/** La última lista de ODD que bajó este celular: con ella la app abre sin señal. Se borra al salir. */
const CLAVE_ULTIMA = 'madeci:ruta:ultima';
interface Ultima {
  empresaId: string;
  guardadaEn: string;
  datos: MisRutas;
}
function leerUltima(): Ultima | null {
  try {
    const t = window.localStorage.getItem(CLAVE_ULTIMA);
    return t ? (JSON.parse(t) as Ultima) : null;
  } catch {
    return null;
  }
}
function guardarUltima(u: Ultima | null) {
  try {
    if (u) window.localStorage.setItem(CLAVE_ULTIMA, JSON.stringify(u));
    else window.localStorage.removeItem(CLAVE_ULTIMA);
  } catch {
    // Sin almacenamiento: sin señal no habrá lista guardada, nada más.
  }
}

/** Empresa de la app: la de la sesión o, sin señal, la de la última lista guardada. */
const EmpresaRuta = createContext('');
const useEmpresaRuta = () => useContext(EmpresaRuta);

function fijarEnHead(tag: 'link' | 'meta', clave: string, valor: string, atributo: string) {
  const sel = tag === 'link' ? `link[rel="${clave}"]` : `meta[name="${clave}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute(tag === 'link' ? 'rel' : 'name', clave);
    document.head.appendChild(el);
  }
  el.setAttribute(atributo, valor);
}

/** Igual que la app del vendedor: su manifiesto y, en su carpeta, su service worker. */
function instalable() {
  try {
    // Si se llegó por el «#» desde el ERP, el manifiesto puesto al cargar sería el de Bodega.
    fijarEnHead('link', 'manifest', '/reparto.webmanifest', 'href');
    fijarEnHead('link', 'apple-touch-icon', '/reparto-180.png', 'href');
    fijarEnHead('meta', 'apple-mobile-web-app-title', 'MADECI Reparto', 'content');
    if (
      'serviceWorker' in navigator &&
      window.isSecureContext &&
      window.location.pathname.startsWith('/app/reparto/')
    ) {
      // Al abrir busca versión nueva; si llega una, recarga UNA vez para correr el código nuevo.
      const habia = !!navigator.serviceWorker.controller;
      let recargada = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (habia && !recargada) {
          recargada = true;
          window.location.reload();
        }
      });
      void navigator.serviceWorker
        .register('/sw-reparto.js', { scope: '/app/reparto/', updateViaCache: 'none' })
        .then((r) => r.update())
        .catch(() => undefined);
    }
  } catch {
    /* sin instalación: la app sigue funcionando en el navegador */
  }
}

// ---------------------------------------------------------------- pantalla

export function Ruta({ sinConexion = false }: { sinConexion?: boolean }) {
  const s = useSesion();
  const qc = useQueryClient();
  const [ultima] = useState(leerUltima);
  const empresaId = s.empresa?.id ?? (sinConexion ? ultima?.empresaId : undefined) ?? '';
  useEffect(instalable, []);
  // Sin señal: apenas vuelve, se revisa la sesión y la app sigue con datos frescos.
  useEffect(() => {
    if (!sinConexion) return;
    const revisar = () => void qc.invalidateQueries({ queryKey: ['sesion'] });
    const t = window.setInterval(revisar, 20_000);
    window.addEventListener('online', revisar);
    return () => {
      window.clearInterval(t);
      window.removeEventListener('online', revisar);
    };
  }, [sinConexion, qc]);
  if (!empresaId)
    return (
      <div className="ruta">
        <header className="ruta-cabecera">
          <strong>MADECI · Reparto</strong>
          <span className="ruta-senal" data-estado="sin-senal" role="status">
            Sin señal
          </span>
        </header>
        <main className="ruta-cuerpo">
          <section className="ruta-recuadro ruta-alerta">
            <h2>Sin señal</h2>
            <p>
              Este celular no tiene tus rutas guardadas. Abre la app con señal antes de salir y
              después funciona sin ella.
            </p>
          </section>
        </main>
      </div>
    );
  return (
    <EmpresaRuta.Provider value={empresaId}>
      <AppRuta sinConexion={sinConexion} ultima={ultima?.empresaId === empresaId ? ultima : null} />
    </EmpresaRuta.Provider>
  );
}

function AppRuta({ sinConexion, ultima }: { sinConexion: boolean; ultima: Ultima | null }) {
  const s = useSesion();
  const empresaId = useEmpresaRuta();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['emp', empresaId, 'ruta', 'mis-ordenes'],
    queryFn: () => api.get<MisRutas>('/ruta/mis-ordenes', { query: { empresaId } }),
    refetchInterval: 30_000,
    enabled: !sinConexion,
  });
  useEffect(() => {
    if (q.data) guardarUltima({ empresaId, guardadaEn: new Date().toISOString(), datos: q.data });
  }, [q.data, empresaId]);
  // Sin señal (al abrir o al caerse la red) se trabaja con la última lista que bajó este celular.
  const sinRed = sinConexion || (q.error instanceof ErrorApi && q.error.status === 0);
  const datos = q.data ?? (sinRed ? ultima?.datos : undefined);
  const refrescar = useCallback(
    () => void qc.invalidateQueries({ queryKey: ['emp', empresaId, 'ruta'] }),
    [qc, empresaId],
  );
  const cola = useCola(empresaId, refrescar);
  const { conciliar } = cola;
  // dataUpdatedAt: también cuando la lista llega igual (lo enviado ya venía en una vuelta anterior).
  useEffect(() => {
    if (q.data) conciliar(q.data.ordenes);
  }, [q.data, q.dataUpdatedAt, conciliar]);
  const [odId, setOdId] = useState<string | null>(null);
  const [notaId, setNotaId] = useState<string | null>(null);
  const [rendida, setRendida] = useState<number | null>(null);
  const ordenes = datos?.ordenes ?? [];
  const od = ordenes.find((o) => o.odId === odId) ?? (ordenes.length === 1 ? ordenes[0]! : null);
  const parada = od?.paradas.find((p) => p.notaId === notaId) ?? null;
  const senal = sinRed || !cola.enLinea ? 'sin-senal' : cola.porEnviar ? 'pendiente' : 'ok';
  return (
    <div className="ruta">
      <header className="ruta-cabecera">
        <strong>MADECI · Reparto</strong>
        <span className="ruta-usuario">{datos?.personal?.nombre ?? s.usuario?.nombre ?? ''}</span>
        <span className="ruta-senal" data-estado={senal} role="status">
          {senal === 'sin-senal'
            ? `Sin señal${cola.porEnviar ? ` · ${cola.porEnviar} por enviar` : ''}`
            : senal === 'pendiente'
              ? `${cola.porEnviar} por enviar`
              : 'En línea'}
        </span>
        {s.puede('ventas.nota.ver') && (
          // Fuera de /app/reparto/: el ERP de escritorio no es parte de la app instalada.
          <a className="ruta-enlace" href="/#/">
            ERP
          </a>
        )}
        {!sinConexion && (
          <button
            type="button"
            className="ruta-enlace"
            onClick={() => {
              if (cola.porEnviar && !window.confirm('Hay registros sin enviar. ¿Salir igual?'))
                return;
              guardarUltima(null);
              void s.cerrar();
            }}
          >
            Salir
          </button>
        )}
      </header>
      <main className="ruta-cuerpo">
        {sinRed && datos && !q.data && ultima && (
          <p className="ruta-apagado" role="status">
            Sin señal · lista guardada el {fechaHora(ultima.guardadaEn)} · lo que registres se envía
            al volver la señal.
          </p>
        )}
        {q.isError && !datos ? (
          <PanelError error={q.error} />
        ) : !datos ? (
          <p className="ruta-apagado">Cargando…</p>
        ) : !datos.personal ? (
          <section className="ruta-recuadro ruta-alerta">
            <h2>Usuario sin ficha de ruta</h2>
            <p>
              Tu usuario no está ligado a una ficha de chofer o peoneta. Pide a la oficina que te
              ligue en <b>Logística › Choferes y peonetas</b>.
            </p>
          </section>
        ) : rendida !== null ? (
          <section className="ruta-recuadro ruta-ok" role="status">
            <h2>ODD N° {rendida} rendida</h2>
            <p>
              Entrega el efectivo y los cheques en <b>tesorería</b>. Ahí se cuentan y se abonan a
              cada cliente.
            </p>
            <button type="button" className="ruta-secundario" onClick={() => setRendida(null)}>
              Listo
            </button>
          </section>
        ) : parada && od ? (
          <VistaParada od={od} parada={parada} cola={cola} alVolver={() => setNotaId(null)} />
        ) : od ? (
          <VistaOd
            od={od}
            cola={cola}
            varias={ordenes.length > 1}
            alVolver={() => setOdId(null)}
            alAbrir={setNotaId}
            alRendir={() => {
              setRendida(od.numero);
              setOdId(null);
              refrescar();
            }}
          />
        ) : !ordenes.length ? (
          <section className="ruta-recuadro">
            <h2>Mis rutas</h2>
            <p className="ruta-apagado">No tienes ODD en ruta.</p>
          </section>
        ) : (
          <section className="ruta-recuadro">
            <h2>Mis rutas</h2>
            <ul className="ruta-lista">
              {ordenes.map((o) => (
                <li key={o.odId}>
                  <button type="button" onClick={() => setOdId(o.odId)}>
                    <span className="ruta-numero">{o.paradas.length}</span>
                    <span>
                      <span className="ruta-titulo">
                        ODD N° {o.numero}
                        {o.nombre ? ` · ${o.nombre}` : ''}
                      </span>
                      <span className="ruta-apagado">
                        {o.patente ?? 'sin patente'} · {fecha(o.fechaNegocio)} ·{' '}
                        {o.estado === 'RENDIDA' ? 'rendida' : 'en ruta'}
                      </span>
                    </span>
                    <span>›</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}

type Cola = ReturnType<typeof useCola>;

/** Lo que el celular sabe de la parada: lo del servidor + lo que el servidor todavía no muestra. */
function estadoLocal(p: Parada, od: OdRuta, cola: Cola) {
  const pendientes = cola.cola.filter(
    (x) => x.odId === od.odId && x.notaId === p.notaId && !yaEnLaParada(x, p),
  );
  const entregaPendiente = pendientes.find((x) => x.tipo === 'entrega') ?? null;
  const cobrosPendientes = pendientes.filter((x) => x.tipo === 'cobro');
  const vigentes = p.cobros.filter((c) => !c.anulado);
  const cobrado =
    vigentes.reduce((t, c) => t + Number(c.monto), 0) +
    cobrosPendientes.reduce((t, c) => t + Number(c.cuerpo['monto']), 0);
  return {
    marcada: p.marcada || !!entregaPendiente,
    entregaPendiente,
    cobrosPendientes,
    vigentes,
    cobrado,
  };
}

function VistaOd({
  od,
  cola,
  varias,
  alVolver,
  alAbrir,
  alRendir,
}: {
  od: OdRuta;
  cola: Cola;
  varias: boolean;
  alVolver: () => void;
  alAbrir: (notaId: string) => void;
  alRendir: () => void;
}) {
  const empresaId = useEmpresaRuta();
  const [error, setError] = useState<unknown>(null);
  const [rindiendo, setRindiendo] = useState(false);
  const locales = od.paradas.map((p) => ({ p, l: estadoLocal(p, od, cola) }));
  const sinMarcar = locales.filter((x) => !x.l.marcada).length;
  const porEnviar = cola.cola.filter((x) => x.odId === od.odId && !x.enviado).length;
  const totales = useMemo(() => {
    const t: Record<Medio, { n: number; monto: number }> = {
      EFECTIVO: { n: 0, monto: 0 },
      CHEQUE: { n: 0, monto: 0 },
      TRANSFERENCIA: { n: 0, monto: 0 },
    };
    for (const p of od.paradas)
      for (const c of p.cobros.filter((x) => !x.anulado)) {
        t[c.medio].n += 1;
        t[c.medio].monto += Number(c.monto);
      }
    for (const x of cola.cola.filter((y) => y.odId === od.odId && y.tipo === 'cobro')) {
      const p = od.paradas.find((y) => y.notaId === x.notaId);
      if (p && yaEnLaParada(x, p)) continue;
      const m = x.cuerpo['medio'] as Medio;
      t[m].n += 1;
      t[m].monto += Number(x.cuerpo['monto']);
    }
    return t;
  }, [od, cola.cola]);
  const aCobrar = od.paradas.reduce((t, p) => t + Number(p.aCobrar.total), 0);
  const rendir = async () => {
    setRindiendo(true);
    setError(null);
    try {
      await api.post(
        `/ruta/ordenes/${od.odId}/rendir`,
        { empresaId, version: od.version },
        { idempotencia: nuevaClaveIdempotencia('rendir') },
      );
      alRendir();
    } catch (e) {
      setError(e);
    } finally {
      setRindiendo(false);
    }
  };
  return (
    <>
      {varias && (
        <button type="button" className="ruta-volver" onClick={alVolver}>
          ‹ Mis rutas
        </button>
      )}
      <section className="ruta-recuadro">
        <h2>
          ODD N° {od.numero}
          {od.nombre ? ` · ${od.nombre}` : ''}
        </h2>
        <p className="ruta-apagado">
          {od.patente ?? 'sin patente'} · {fecha(od.fechaNegocio)} · {od.paradas.length} parada(s) ·
          por cobrar {pesos(String(aCobrar))}
        </p>
        <ul className="ruta-lista">
          {locales.map(({ p, l }) => (
            <li key={p.notaId} data-marcada={l.marcada}>
              <button type="button" onClick={() => alAbrir(p.notaId)}>
                <span className="ruta-numero">{l.marcada ? '✓' : p.parada}</span>
                <span>
                  <span className="ruta-titulo">{p.cliente}</span>
                  <span className="ruta-apagado">
                    <span className="ruta-chip" data-modo={p.aCobrar.modo}>
                      {NOMBRE_MODO[p.aCobrar.modo]}
                    </span>
                    {NOMBRE_ESTADO[p.estado] && (
                      <span className="ruta-chip" data-estado={p.estado}>
                        {NOMBRE_ESTADO[p.estado]}
                      </span>
                    )}
                    {p.direccion ?? ''}
                  </span>
                </span>
                <span className="ruta-monto">
                  {p.aCobrar.modo === 'CREDITO' && !l.cobrado ? '—' : pesos(String(l.cobrado))}
                  {p.aCobrar.modo !== 'CREDITO' && (
                    <span className="ruta-apagado" style={{ display: 'block' }}>
                      de {pesos(p.aCobrar.total)}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </section>
      {od.estado === 'EN_RUTA' && (
        <section className="ruta-recuadro">
          <h2>Rendición</h2>
          <table className="ruta-tabla">
            <thead>
              <tr>
                <th>Medio</th>
                <th className="num">Cant.</th>
                <th className="num">Monto</th>
              </tr>
            </thead>
            <tbody>
              {(Object.keys(totales) as Medio[]).map((m) => (
                <tr key={m}>
                  <td>{NOMBRE_MEDIO[m]}</td>
                  <td className="num">{totales[m].n}</td>
                  <td className="num">{pesos(String(totales[m].monto))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="ruta-ayuda">
            Al rendir, entrega en tesorería el efectivo ({pesos(String(totales.EFECTIVO.monto))}) y
            los {totales.CHEQUE.n} cheque(s).
          </p>
          {!!error && <PanelError error={error} alCerrar={() => setError(null)} />}
          <button
            type="button"
            className="ruta-primario"
            disabled={rindiendo || sinMarcar > 0 || porEnviar > 0 || !cola.enLinea}
            onClick={() => void rendir()}
          >
            {rindiendo
              ? 'Rindiendo…'
              : sinMarcar
                ? `Faltan ${sinMarcar} parada(s) por marcar`
                : porEnviar
                  ? `Esperando señal: ${porEnviar} por enviar`
                  : 'Entregar rendición'}
          </button>
        </section>
      )}
      {od.estado === 'RENDIDA' && (
        <section className="ruta-recuadro ruta-ok">
          <h2>Rendida</h2>
          <p>Tesorería todavía no recibe los valores de esta ODD.</p>
        </section>
      )}
    </>
  );
}

function VistaParada({
  od,
  parada: p,
  cola,
  alVolver,
}: {
  od: OdRuta;
  parada: Parada;
  cola: Cola;
  alVolver: () => void;
}) {
  const empresaId = useEmpresaRuta();
  const l = estadoLocal(p, od, cola);
  const enRuta = od.estado === 'EN_RUTA';
  return (
    <>
      <button type="button" className="ruta-volver" onClick={alVolver}>
        ‹ Paradas
      </button>
      <section className="ruta-recuadro">
        <h2>
          Parada {p.parada} · {p.cliente}
        </h2>
        <p className="ruta-apagado">
          {p.numeroVisible}
          {p.direccion ? ` · ${p.direccion}` : ''}
          {p.zona ? ` · ${p.zona}` : ''}
        </p>
        {p.direccion && (
          <a
            className="ruta-secundario"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.direccion)}`}
            target="_blank"
            rel="noreferrer"
          >
            Abrir en el mapa
          </a>
        )}
      </section>
      <Entrega
        od={od}
        parada={p}
        cola={cola}
        marcada={l.marcada}
        pendiente={!!l.entregaPendiente}
        enRuta={enRuta}
      />
      <PorCobrar parada={p} cobrado={l.cobrado} />
      <section className="ruta-recuadro">
        <h2>Cobros de esta parada</h2>
        {!l.vigentes.length && !l.cobrosPendientes.length ? (
          <p className="ruta-apagado">Sin cobros registrados.</p>
        ) : (
          <table className="ruta-tabla">
            <tbody>
              {l.vigentes.map((c) => (
                <FilaCobro
                  key={c.cobroId}
                  od={od}
                  cobro={c}
                  empresaId={empresaId}
                  enRuta={enRuta}
                />
              ))}
              {l.cobrosPendientes.map((x) => (
                <tr key={x.id} data-pendiente="true">
                  <td>
                    {NOMBRE_MEDIO[x.cuerpo['medio'] as Medio]}
                    <div className="ruta-apagado">
                      {x.enviado ? 'enviado' : x.error ? '' : 'por enviar'}
                    </div>
                    {x.error && !x.enviado && <div className="ruta-error">{x.error}</div>}
                  </td>
                  <td className="num">{pesos(String(x.cuerpo['monto']))}</td>
                  <td>
                    {!x.enviado && (
                      <button
                        type="button"
                        className="ruta-secundario"
                        onClick={() => cola.descartar(x.id)}
                      >
                        Quitar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      {enRuta && <NuevoCobro od={od} parada={p} cola={cola} cobrado={l.cobrado} />}
    </>
  );
}

function Entrega({
  od,
  parada: p,
  cola,
  marcada,
  pendiente,
  enRuta,
}: {
  od: OdRuta;
  parada: Parada;
  cola: Cola;
  marcada: boolean;
  pendiente: boolean;
  enRuta: boolean;
}) {
  const empresaId = useEmpresaRuta();
  const [modo, setModo] = useState<'completa' | 'parcial' | 'rechazada' | null>(null);
  const [cantidades, setCantidades] = useState<Record<string, string>>({});
  const [motivo, setMotivo] = useState('');
  // Lo devuelto: ¿lo quiere otro día (vuelve a salir con la misma factura) o va nota de crédito?
  const [destino, setDestino] = useState<'NC' | 'REENTREGA' | null>(null);
  if (marcada)
    return (
      <section className="ruta-recuadro ruta-ok">
        <h2>Entrega</h2>
        <p>
          {pendiente
            ? 'Marcada · por enviar'
            : NOMBRE_ESTADO[p.estado]
              ? `Marcada: ${NOMBRE_ESTADO[p.estado]!.toLowerCase()}`
              : 'Marcada'}
        </p>
      </section>
    );
  if (!enRuta) return null;
  const registrar = (
    lineas?: Array<{ notaLineaId: string; entregado: string; motivoRechazo: string | null }>,
  ) =>
    cola.agregar({
      tipo: 'entrega',
      odId: od.odId,
      notaId: p.notaId,
      ruta: `/ruta/ordenes/${od.odId}/paradas/${p.notaId}/entrega`,
      cuerpo: {
        empresaId,
        ...(lineas ? { lineas } : {}),
        ...(lineas && destino ? { destinoRechazo: destino } : {}),
      },
    });
  const lineasParcial = p.lineas.map((x) => ({
    notaLineaId: x.notaLineaId,
    entregado: soloDigitos(cantidades[x.notaLineaId] ?? x.cargado?.split('.')[0] ?? '0') || '0',
    motivoRechazo: motivo.trim() || null,
  }));
  const devuelve =
    modo === 'rechazada' ||
    (modo === 'parcial' &&
      lineasParcial.some(
        (x, i) => Number(x.entregado) < Number((p.lineas[i]!.cargado ?? '0').split('.')[0]),
      ));
  return (
    <section className="ruta-recuadro">
      <h2>Entrega</h2>
      <div className="ruta-opciones">
        {(
          [
            ['completa', 'Completa'],
            ['parcial', 'Parcial'],
            ['rechazada', 'Rechazada'],
          ] as const
        ).map(([v, t]) => (
          <button key={v} type="button" aria-pressed={modo === v} onClick={() => setModo(v)}>
            {t}
          </button>
        ))}
      </div>
      {modo === 'parcial' && (
        <table className="ruta-tabla" style={{ marginTop: 10 }}>
          <thead>
            <tr>
              <th>Producto</th>
              <th className="num">Cargado</th>
              <th className="num">Entregó</th>
            </tr>
          </thead>
          <tbody>
            {p.lineas.map((x) => (
              <tr key={x.notaLineaId}>
                <td>
                  {x.sku} {x.descripcion}
                </td>
                <td className="num">{(x.cargado ?? '0').split('.')[0]}</td>
                <td className="num">
                  <input
                    inputMode="numeric"
                    aria-label={`Entregó ${x.sku}`}
                    value={cantidades[x.notaLineaId] ?? (x.cargado ?? '0').split('.')[0]}
                    onChange={(e) =>
                      setCantidades((c) => ({ ...c, [x.notaLineaId]: soloDigitos(e.target.value) }))
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(modo === 'parcial' || modo === 'rechazada') && (
        <label className="ruta-campo">
          Motivo{modo === 'rechazada' ? '' : ' de lo devuelto'}
          <input value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={200} />
        </label>
      )}
      {devuelve && (
        <div className="ruta-campo">
          Lo devuelto, ¿lo quiere otro día?
          <div className="ruta-opciones">
            <button type="button" aria-pressed={destino === 'NC'} onClick={() => setDestino('NC')}>
              No lo quiere
            </button>
            <button
              type="button"
              aria-pressed={destino === 'REENTREGA'}
              onClick={() => setDestino('REENTREGA')}
            >
              Lo quiere otro día
            </button>
          </div>
        </div>
      )}
      {modo && (
        <button
          type="button"
          className="ruta-primario"
          disabled={(modo !== 'completa' && motivo.trim().length < 3) || (devuelve && !destino)}
          onClick={() =>
            modo === 'completa'
              ? registrar()
              : registrar(
                  modo === 'rechazada'
                    ? p.lineas.map((x) => ({
                        notaLineaId: x.notaLineaId,
                        entregado: '0',
                        motivoRechazo: motivo.trim(),
                      }))
                    : lineasParcial,
                )
          }
        >
          {modo === 'completa'
            ? 'Marcar entregada completa'
            : modo === 'parcial'
              ? 'Marcar parcial'
              : 'Marcar rechazada'}
        </button>
      )}
    </section>
  );
}

function PorCobrar({ parada: p, cobrado }: { parada: Parada; cobrado: number }) {
  const { modo, total, documentos } = p.aCobrar;
  if (modo === 'CREDITO')
    return (
      <section className="ruta-recuadro">
        <h2>Por cobrar</h2>
        <p className="ruta-apagado">A crédito: no se cobra en esta entrega.</p>
      </section>
    );
  return (
    <section className={`ruta-recuadro${modo === 'COBRANZA' ? ' ruta-alerta' : ''}`}>
      <h2>
        {modo === 'COBRANZA' ? 'Cobranza: paga lo pendiente al recibir' : 'Por cobrar (contado)'}
      </h2>
      <table className="ruta-tabla">
        <thead>
          <tr>
            <th>Documento</th>
            <th>Vence</th>
            <th className="num">Saldo</th>
          </tr>
        </thead>
        <tbody>
          {documentos.map((d) => (
            <tr key={d.documentoId}>
              <td>
                {d.tipoDte === 39 ? 'Boleta' : d.tipoDte === 0 ? 'Vale' : 'Factura'} {d.folio ?? ''}
                {d.deEstaEntrega && <div className="ruta-apagado">esta entrega</div>}
              </td>
              <td>
                {fecha(d.fechaVencimiento)}
                {d.diasVencido > 0 && (
                  <div className="ruta-error">{d.diasVencido} días vencida</div>
                )}
              </td>
              <td className="num">{pesos(d.saldo)}</td>
            </tr>
          ))}
          {!documentos.length && (
            <tr>
              <td colSpan={3} className="ruta-apagado">
                Sin saldo pendiente.
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2}>Total a cobrar</td>
            <td className="num">{pesos(total)}</td>
          </tr>
          <tr>
            <td colSpan={2}>Cobrado</td>
            <td className="num">{pesos(String(cobrado))}</td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
}

function FilaCobro({
  od,
  cobro: c,
  empresaId,
  enRuta,
}: {
  od: OdRuta;
  cobro: Cobro;
  empresaId: string;
  enRuta: boolean;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [foto, setFoto] = useState<string | null>(null);
  useEffect(() => () => void (foto && URL.revokeObjectURL(foto)), [foto]);
  const anular = async () => {
    const motivo = window.prompt('¿Por qué se anula este cobro?');
    if (!motivo || motivo.trim().length < 3) return;
    try {
      await api.post(
        `/ruta/ordenes/${od.odId}/cobros/${c.cobroId}/anular`,
        { empresaId, motivo: motivo.trim() },
        { idempotencia: nuevaClaveIdempotencia('anular-cobro') },
      );
      await qc.invalidateQueries({ queryKey: ['emp', empresaId, 'ruta'] });
    } catch (e) {
      setError(e);
    }
  };
  const verFoto = async () => {
    try {
      const b = await pedirArchivo('GET', `/ruta/ordenes/${od.odId}/cobros/${c.cobroId}/foto`, {
        query: { empresaId },
      });
      setFoto(URL.createObjectURL(b));
    } catch (e) {
      setError(e);
    }
  };
  return (
    <tr>
      <td>
        {NOMBRE_MEDIO[c.medio]}
        <div className="ruta-apagado">
          {c.medio === 'CHEQUE'
            ? `${c.banco ?? ''} N° ${c.numeroDocumento ?? c.referencia ?? ''} · ${fecha(c.fechaDocumento)}`
            : (c.referencia ?? '')}
        </div>
        {foto && <img className="ruta-foto" src={foto} alt="Foto del comprobante" />}
        {!!error && <PanelError error={error} alCerrar={() => setError(null)} />}
      </td>
      <td className="num">{pesos(c.monto)}</td>
      <td>
        {c.tieneFoto && !foto && (
          <button type="button" className="ruta-secundario" onClick={() => void verFoto()}>
            Foto
          </button>
        )}
        {enRuta && !c.estadoTesoreria && (
          <button type="button" className="ruta-secundario" onClick={() => void anular()}>
            Anular
          </button>
        )}
      </td>
    </tr>
  );
}

function NuevoCobro({
  od,
  parada: p,
  cola,
  cobrado,
}: {
  od: OdRuta;
  parada: Parada;
  cola: Cola;
  cobrado: number;
}) {
  const empresaId = useEmpresaRuta();
  const total = Number(p.aCobrar.total);
  // Sin total conocido (crédito o sin documento) no se sugiere monto ni se avisa al pasarse.
  const conTotal = p.aCobrar.modo !== 'CREDITO' && total > 0;
  const sugerido = conTotal ? total - cobrado : 0;
  const [medio, setMedio] = useState<Medio>('EFECTIVO');
  const [monto, setMonto] = useState(sugerido > 0 ? String(sugerido) : '');
  const [referencia, setReferencia] = useState('');
  const [banco, setBanco] = useState('');
  const [numero, setNumero] = useState('');
  const [fechaCheque, setFechaCheque] = useState(() => new Date().toLocaleDateString('sv-SE'));
  const [foto, setFoto] = useState<{ tipoMime: 'image/jpeg'; base64: string } | null>(null);
  const [errorFoto, setErrorFoto] = useState<string | null>(null);
  useEffect(() => setMonto(sugerido > 0 ? String(sugerido) : ''), [sugerido]);
  const cheque = medio === 'CHEQUE';
  const valido =
    Number(monto) > 0 &&
    (!cheque || (banco.trim().length >= 2 && numero.trim() !== '' && !!fechaCheque));
  const registrar = () => {
    // Pasarse del total casi siempre es el mismo pago registrado dos veces.
    if (
      conTotal &&
      Number(monto) > sugerido &&
      !window.confirm(
        sugerido > 0
          ? `Falta ${pesos(String(sugerido))} y estás registrando ${pesos(monto)}. ¿Registrar igual?`
          : `Ya está cobrado el total de esta parada (${pesos(String(total))}). ¿Registrar otro cobro de ${pesos(monto)}?`,
      )
    )
      return;
    cola.agregar({
      tipo: 'cobro',
      odId: od.odId,
      notaId: p.notaId,
      ruta: `/ruta/ordenes/${od.odId}/cobros`,
      cuerpo: {
        empresaId,
        cobroId: crypto.randomUUID(),
        notaId: p.notaId,
        medio,
        monto: soloDigitos(monto),
        ...(referencia.trim() ? { referencia: referencia.trim() } : {}),
        ...(cheque
          ? { banco: banco.trim(), numeroDocumento: numero.trim(), fechaDocumento: fechaCheque }
          : {}),
        ...(foto ? { foto } : {}),
      },
    });
    setReferencia('');
    setBanco('');
    setNumero('');
    setFoto(null);
  };
  return (
    <section className="ruta-recuadro">
      <h2>Registrar cobro</h2>
      {conTotal &&
        (sugerido <= 0 ? (
          <p className="ruta-ayuda">Cobrado completo.</p>
        ) : cobrado > 0 ? (
          <p className="ruta-ayuda">
            <b>Falta {pesos(String(sugerido))}</b> · si paga el resto con otro medio, elígelo y
            regístralo.
          </p>
        ) : (
          <p className="ruta-ayuda">
            ¿Paga con dos medios? Registra uno con su monto y después el otro.
          </p>
        ))}
      <div className="ruta-opciones">
        {(Object.keys(NOMBRE_MEDIO) as Medio[]).map((m) => (
          <button key={m} type="button" aria-pressed={medio === m} onClick={() => setMedio(m)}>
            {NOMBRE_MEDIO[m]}
          </button>
        ))}
      </div>
      <label className="ruta-campo">
        Monto
        <input
          inputMode="numeric"
          value={monto}
          onChange={(e) => setMonto(soloDigitos(e.target.value))}
          placeholder="0"
        />
      </label>
      {cheque && (
        <>
          <div className="ruta-fila">
            <label className="ruta-campo">
              Banco
              <input value={banco} onChange={(e) => setBanco(e.target.value)} maxLength={60} />
            </label>
            <label className="ruta-campo">
              N° cheque
              <input
                inputMode="numeric"
                value={numero}
                onChange={(e) => setNumero(e.target.value)}
                maxLength={30}
              />
            </label>
          </div>
          <label className="ruta-campo">
            Fecha del cheque
            <input
              type="date"
              value={fechaCheque}
              onChange={(e) => setFechaCheque(e.target.value)}
            />
          </label>
        </>
      )}
      {medio !== 'EFECTIVO' && (
        <>
          {medio === 'TRANSFERENCIA' && (
            <label className="ruta-campo">
              N° de operación (opcional)
              <input
                value={referencia}
                onChange={(e) => setReferencia(e.target.value)}
                maxLength={60}
              />
            </label>
          )}
          <label className="ruta-campo">
            Foto {cheque ? 'del cheque' : 'del comprobante'}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => {
                const f = e.target.files?.[0];
                setErrorFoto(null);
                if (f)
                  reducirFoto(f)
                    .then(setFoto)
                    .catch((x: Error) => setErrorFoto(x.message));
              }}
            />
          </label>
          {foto && (
            <img
              className="ruta-foto"
              src={`data:${foto.tipoMime};base64,${foto.base64}`}
              alt="Foto adjunta"
            />
          )}
          {errorFoto && <p className="ruta-error">{errorFoto}</p>}
        </>
      )}
      <button type="button" className="ruta-primario" disabled={!valido} onClick={registrar}>
        Registrar {NOMBRE_MEDIO[medio].toLowerCase()}{' '}
        {Number(monto) > 0 ? pesos(soloDigitos(monto)) : ''}
      </button>
    </section>
  );
}
